import { useState, useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchAllCities, filterCities, toTitleCase, type IBGECity } from "@/lib/ibgeCities";

// Lista de estados brasileiros
const BRAZILIAN_STATES = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" },
];

interface StateCitySelectorProps {
  state: string;
  city: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  stateLabel?: string;
  cityLabel?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

// Cache global para cidades por estado
const citiesByStateCache: Record<string, IBGECity[]> = {};

export function StateCitySelector({
  state,
  city,
  onStateChange,
  onCityChange,
  stateLabel = "Estado",
  cityLabel = "Cidade",
  required = false,
  className,
  disabled = false,
}: StateCitySelectorProps) {
  const [cities, setCities] = useState<IBGECity[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  
  // Local state to track selection
  const [localState, setLocalState] = useState(state);

  // Sync local state with prop
  useEffect(() => {
    setLocalState(state);
  }, [state]);

  // Buscar cidades quando UF muda (usando localState)
  useEffect(() => {
    if (!localState) {
      setCities([]);
      return;
    }

    // Verificar cache
    if (citiesByStateCache[localState]) {
      setCities(citiesByStateCache[localState]);
      return;
    }

    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const allCities = await fetchAllCities();
        const stateCities = allCities.filter(c => c.uf === localState);
        citiesByStateCache[localState] = stateCities;
        setCities(stateCities);
      } catch (error) {
        console.error("Erro ao buscar cidades:", error);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, [localState]);

  // Filtrar cidades conforme digitação
  const filteredCities = useMemo(() => {
    return filterCities(cities, citySearch, 100);
  }, [cities, citySearch]);

  const handleStateChange = (newState: string) => {
    setLocalState(newState);
    onStateChange(newState);
    // Reset cidade quando estado muda
    if (newState !== state) {
      onCityChange("");
      setCitySearch("");
    }
  };

  return (
    <div className={cn("grid gap-4 md:grid-cols-2", className)}>
      {/* Estado */}
      <div className="space-y-2 min-w-0">
        <Label>
          {stateLabel} {required && "*"}
        </Label>
        <Select 
          value={localState || undefined} 
          onValueChange={handleStateChange} 
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione o estado" />
          </SelectTrigger>
          <SelectContent>
            {BRAZILIAN_STATES.map((uf) => (
              <SelectItem key={uf.sigla} value={uf.sigla}>
                {uf.sigla} - {uf.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cidade */}
      <div className="space-y-2 min-w-0">
        <Label>
          {cityLabel} {required && "*"}
        </Label>
        <Popover open={cityOpen} onOpenChange={setCityOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={cityOpen}
              className="w-full justify-between font-normal"
              disabled={!localState || loadingCities || disabled}
            >
              {loadingCities ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Carregando...</span>
                </div>
              ) : city ? (
                toTitleCase(city)
              ) : !localState ? (
                <span className="text-muted-foreground">Selecione o estado primeiro</span>
              ) : (
                <span className="text-muted-foreground">Buscar cidade...</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-popover" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Digite para buscar..."
                value={citySearch}
                onValueChange={setCitySearch}
              />
              <CommandList>
                <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                <CommandGroup>
                  {filteredCities.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.nome}
                      onSelect={() => {
                        onCityChange(c.nome);
                        setCityOpen(false);
                        setCitySearch("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          city?.toLowerCase() === c.nome.toLowerCase() ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {c.nome}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
